import { devLog, devError } from '../utils/devLogger';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../firebase';

class IssuesService {
  async createIssue(issue) {

    const enrichedIssue = {
      ...issue,
      status: issue.status || 'open',
      comments: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'issues'), enrichedIssue);

    // Notify facilitators about the new support issue
    try {
      await this.notifyFacilitators(docRef.id, enrichedIssue);
    } catch (err) {
      devError('Error notifying facilitators:', err);
      // Don't fail the issue creation if notification fails
    }

    return {
      id: docRef.id,
      ...enrichedIssue
    };
  }

  // Notify facilitators assigned to the learner's cohort about a new issue
  async notifyFacilitators(issueId, issue) {
    if (!issue.reportedBy) return;

    try {
      // Get the learner's user document to find their cohort
      const userDoc = await getDoc(doc(db, 'users', issue.reportedBy));
      if (!userDoc.exists()) {
        devLog('User not found for issue notification');
        return;
      }

      const userData = userDoc.data();
      const cohortId = userData.cohortId;

      if (!cohortId) {
        devLog('User has no assigned cohort');
        return;
      }

      // Get the cohort to find assigned facilitators
      const cohortDoc = await getDoc(doc(db, 'cohorts', cohortId));
      if (!cohortDoc.exists()) {
        devLog('Cohort not found');
        return;
      }

      const cohortData = cohortDoc.data();
      const facilitatorIds = cohortData.facilitators || [];

      if (facilitatorIds.length === 0) {
        devLog('No facilitators assigned to cohort');
        return;
      }

      // Get facilitator emails
      const facilitatorEmails = [];
      for (const facId of facilitatorIds) {
        const facDoc = await getDoc(doc(db, 'users', facId));
        if (facDoc.exists() && facDoc.data().email) {
          facilitatorEmails.push(facDoc.data().email);
        }
      }

      if (facilitatorEmails.length === 0) {
        devLog('No facilitator emails found');
        return;
      }

      // Store the notification for facilitators (they'll see it in their dashboard)
      await addDoc(collection(db, 'notifications'), {
        type: 'support_issue',
        title: `New Support Issue: ${issue.title}`,
        message: `${issue.reportedByName || 'A learner'} has reported a new ${issue.priority || 'medium'} priority issue: ${issue.title}`,
        issueId: issueId,
        recipientIds: facilitatorIds,
        recipientEmails: facilitatorEmails,
        cohortId: cohortId,
        cohortName: cohortData.name,
        priority: issue.priority || 'medium',
        category: issue.category || 'general',
        reportedBy: issue.reportedBy,
        reportedByName: issue.reportedByName,
        reportedByEmail: issue.reportedByEmail,
        read: false,
        createdAt: serverTimestamp()
      });

      // External support email routing is handled by the Firestore trigger on issues/{issueId}.
      // The in-app notification above remains for facilitator visibility in the LMS.

    } catch (err) {
      devError('Error in notifyFacilitators:', err);
    }
  }

  async updateIssue(issueId, updates) {
    const issueRef = doc(db, 'issues', issueId);
    await updateDoc(issueRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    // Return the locally-known state — the onSnapshot listener will deliver the authoritative version
    return { id: issueId, ...updates };
  }

  async addComment(issueId, comment) {

    const issueRef = doc(db, 'issues', issueId);
    const commentPayload = {
      ...comment,
      createdAt: serverTimestamp()
    };

    const commentRef = await addDoc(collection(db, 'issues', issueId, 'comments'), commentPayload);

    await updateDoc(issueRef, {
      commentCount: increment(1),
      updatedAt: serverTimestamp()
    });

    return {
      id: commentRef.id,
      ...commentPayload,
      createdAt: new Date()
    };
  }

  subscribeToIssueComments(issueId, callback, onError) {
    const commentsQuery = query(
      collection(db, 'issues', issueId, 'comments'),
      orderBy('createdAt', 'asc')
    );

    return onSnapshot(
      commentsQuery,
      (snapshot) => {
        const comments = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        callback(comments);
      },
      (error) => {
        devError('Error loading issue comments:', error);
        if (onError) onError(error);
      }
    );
  }

  subscribeToUserIssues(userId, callback) {

    const issuesQuery = query(
      collection(db, 'issues'),
      where('reportedBy', '==', userId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(issuesQuery, (snapshot) => {
      const issues = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      callback(issues);
    });
  }

  subscribeToAllIssues(callback) {
    const issuesQuery = query(
      collection(db, 'issues'),
      where('status', '!=', 'closed'),
      orderBy('status'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    return onSnapshot(issuesQuery, (snapshot) => {
      const issues = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      callback(issues);
    });
  }

  async fetchIssues(filters = {}) {

    const constraints = [];
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.priority) {
      constraints.push(where('priority', '==', filters.priority));
    }
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }

    let issuesQuery = query(collection(db, 'issues'), orderBy('createdAt', 'desc'));
    if (constraints.length > 0) {
      issuesQuery = query(collection(db, 'issues'), ...constraints, orderBy('createdAt', 'desc'));
    }

    const snapshot = await getDocs(issuesQuery);
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  }

  getCategories() {
    return [
      { id: 'technical', name: 'Technical Issue', color: 'danger' },
      { id: 'content', name: 'Content Request', color: 'info' },
      { id: 'account', name: 'Account Issue', color: 'warning' },
      { id: 'general', name: 'General Inquiry', color: 'secondary' }
    ];
  }

  getPriorities() {
    return [
      { id: 'low', name: 'Low', color: 'success' },
      { id: 'medium', name: 'Medium', color: 'warning' },
      { id: 'high', name: 'High', color: 'danger' },
      { id: 'critical', name: 'Critical', color: 'dark' }
    ];
  }

  getStatuses() {
    return [
      { id: 'open', name: 'Open', color: 'primary' },
      { id: 'in_progress', name: 'In Progress', color: 'warning' },
      { id: 'resolved', name: 'Resolved', color: 'success' },
      { id: 'closed', name: 'Closed', color: 'secondary' }
    ];
  }
}

const issuesService = new IssuesService();

export default issuesService;
